// Purpose: Centralized video playback state management
// Audience: Components that need to play videos with concurrency control
// Features:
// - Limits concurrent video playback (default: 3 videos)
// - Automatic cleanup when components unmount
// - Proper React state management (no global pollution)
// - Performance optimization for video-heavy pages

import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';

// Action types for the reducer
const VIDEO_ACTIONS = {
  REGISTER_VIDEO: 'REGISTER_VIDEO',
  UNREGISTER_VIDEO: 'UNREGISTER_VIDEO',
  START_PLAYING: 'START_PLAYING',
  STOP_PLAYING: 'STOP_PLAYING',
  SET_MAX_CONCURRENT: 'SET_MAX_CONCURRENT'
};

// Initial state
const initialState = {
  registeredVideos: new Map(), // videoId -> { element, isPlaying, metadata }
  playingVideos: new Set(),    // Set of videoIds currently playing
  maxConcurrent: 3             // Maximum concurrent videos
};

// Reducer for video state management
function videoPlaybackReducer(state, action) {
  switch (action.type) {
    case VIDEO_ACTIONS.REGISTER_VIDEO: {
      const { videoId, element, metadata } = action.payload;
      const newRegisteredVideos = new Map(state.registeredVideos);
      newRegisteredVideos.set(videoId, {
        element,
        isPlaying: false,
        metadata: metadata || {}
      });
      return {
        ...state,
        registeredVideos: newRegisteredVideos
      };
    }

    case VIDEO_ACTIONS.UNREGISTER_VIDEO: {
      const { videoId } = action.payload;
      const newRegisteredVideos = new Map(state.registeredVideos);
      const newPlayingVideos = new Set(state.playingVideos);
      
      newRegisteredVideos.delete(videoId);
      newPlayingVideos.delete(videoId);
      
      return {
        ...state,
        registeredVideos: newRegisteredVideos,
        playingVideos: newPlayingVideos
      };
    }

    case VIDEO_ACTIONS.START_PLAYING: {
      const { videoId } = action.payload;
      const newRegisteredVideos = new Map(state.registeredVideos);
      const newPlayingVideos = new Set(state.playingVideos);
      
      // Update the video's playing status
      const videoData = newRegisteredVideos.get(videoId);
      if (videoData) {
        newRegisteredVideos.set(videoId, {
          ...videoData,
          isPlaying: true
        });
        newPlayingVideos.add(videoId);
      }
      
      return {
        ...state,
        registeredVideos: newRegisteredVideos,
        playingVideos: newPlayingVideos
      };
    }

    case VIDEO_ACTIONS.STOP_PLAYING: {
      const { videoId } = action.payload;
      const newRegisteredVideos = new Map(state.registeredVideos);
      const newPlayingVideos = new Set(state.playingVideos);
      
      // Update the video's playing status
      const videoData = newRegisteredVideos.get(videoId);
      if (videoData) {
        newRegisteredVideos.set(videoId, {
          ...videoData,
          isPlaying: false
        });
      }
      newPlayingVideos.delete(videoId);
      
      return {
        ...state,
        registeredVideos: newRegisteredVideos,
        playingVideos: newPlayingVideos
      };
    }

    case VIDEO_ACTIONS.SET_MAX_CONCURRENT: {
      return {
        ...state,
        maxConcurrent: action.payload.maxConcurrent
      };
    }

    default:
      return state;
  }
}

// Create the context
const VideoPlaybackContext = createContext(null);

// Provider component
export const VideoPlaybackProvider = ({ children, maxConcurrent = 3 }) => {
  const [state, dispatch] = useReducer(videoPlaybackReducer, {
    ...initialState,
    maxConcurrent
  });
  
  // Use ref to store the latest state for callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Register a video element for tracking
  const registerVideo = useCallback((videoId, element, metadata = {}) => {
    dispatch({
      type: VIDEO_ACTIONS.REGISTER_VIDEO,
      payload: { videoId, element, metadata }
    });
  }, []);

  // Unregister a video element (cleanup)
  const unregisterVideo = useCallback((videoId) => {
    dispatch({
      type: VIDEO_ACTIONS.UNREGISTER_VIDEO,
      payload: { videoId }
    });
  }, []);

  // Attempt to play a video with concurrency control
  const playVideo = useCallback(async (videoId) => {
    const currentState = stateRef.current;
    const videoData = currentState.registeredVideos.get(videoId);
    
    if (!videoData || !videoData.element) {
      console.warn(`Video ${videoId} not registered or element not found`);
      return false;
    }

    const { element } = videoData;

    try {
      // Check if we need to pause other videos due to concurrency limit
      if (currentState.playingVideos.size >= currentState.maxConcurrent) {
        // Pause the oldest playing video (first in Set)
        const oldestVideoId = currentState.playingVideos.values().next().value;
        if (oldestVideoId && oldestVideoId !== videoId) {
          const oldestVideoData = currentState.registeredVideos.get(oldestVideoId);
          if (oldestVideoData?.element) {
            try {
              oldestVideoData.element.pause();
              dispatch({
                type: VIDEO_ACTIONS.STOP_PLAYING,
                payload: { videoId: oldestVideoId }
              });
            } catch (error) {
              console.warn(`Failed to pause video ${oldestVideoId}:`, error);
            }
          }
        }
      }

      // Configure video element for autoplay
      element.muted = true;
      element.playsInline = true;
      
      // On iOS, calling load() before play() can help with autoplay
      // Only load if the video hasn't started loading yet
      if (element.readyState === 0) {
        element.load();
      }

      // Attempt to play
      await element.play();
      
      // Mark as playing
      dispatch({
        type: VIDEO_ACTIONS.START_PLAYING,
        payload: { videoId }
      });

      return true;
    } catch (error) {
      console.warn(`Failed to play video ${videoId}:`, error);
      return false;
    }
  }, []);

  // Pause a video
  const pauseVideo = useCallback((videoId) => {
    const currentState = stateRef.current;
    const videoData = currentState.registeredVideos.get(videoId);
    
    if (!videoData || !videoData.element) {
      return;
    }

    try {
      videoData.element.pause();
      dispatch({
        type: VIDEO_ACTIONS.STOP_PLAYING,
        payload: { videoId }
      });
    } catch (error) {
      console.warn(`Failed to pause video ${videoId}:`, error);
    }
  }, []);

  // Pause all videos
  const pauseAllVideos = useCallback(() => {
    const currentState = stateRef.current;
    currentState.playingVideos.forEach(videoId => {
      pauseVideo(videoId);
    });
  }, [pauseVideo]);

  // Get playing status of a video
  const isVideoPlaying = useCallback((videoId) => {
    return state.playingVideos.has(videoId);
  }, [state.playingVideos]);

  // Get statistics
  const getStats = useCallback(() => {
    return {
      totalRegistered: state.registeredVideos.size,
      currentlyPlaying: state.playingVideos.size,
      maxConcurrent: state.maxConcurrent,
      playingVideoIds: Array.from(state.playingVideos)
    };
  }, [state]);

  const contextValue = {
    // State
    registeredVideos: state.registeredVideos,
    playingVideos: state.playingVideos,
    maxConcurrent: state.maxConcurrent,
    
    // Actions
    registerVideo,
    unregisterVideo,
    playVideo,
    pauseVideo,
    pauseAllVideos,
    isVideoPlaying,
    getStats
  };

  return (
    <VideoPlaybackContext.Provider value={contextValue}>
      {children}
    </VideoPlaybackContext.Provider>
  );
};

// Custom hook to use the video playback context
export const useVideoPlayback = () => {
  const context = useContext(VideoPlaybackContext);
  if (!context) {
    throw new Error('useVideoPlayback must be used within a VideoPlaybackProvider');
  }
  return context;
};

// Higher-order component for easy integration
export const withVideoPlayback = (Component) => {
  return function VideoPlaybackWrappedComponent(props) {
    return (
      <VideoPlaybackProvider>
        <Component {...props} />
      </VideoPlaybackProvider>
    );
  };
};

export default VideoPlaybackContext;
